import React, { useEffect } from 'react';
import JoyRide, { Step } from 'react-joyride';
import { IApplicationState, useAppSelector } from '../../libs/store/reducers';
import { joyRideGlobalStyles } from './ProductTourConstants';
import Tooltip from './Tooltip';
import { adminTourStepsOnly, tourSteps } from './TourSteps';
import { storage, STORAGE_CONSTANTS } from '../../libs/services/LocalStorage';
import { useDispatch } from 'react-redux';
import { SidebarActions } from '../../libs/store/actions/SidebarActions';
import { routes } from '../../navigation/Routes';

interface IProductTourProps {
  startTour: boolean;
}

/**
 * The ProductTour component renders a guided tour of the application's features.
 * It uses the react-joyride library to display tooltips and step-by-step instructions.
 *
 * @param {IProductTourProps} props - The component props.
 * @param {boolean} props.startTour - Indicates whether the tour should be started.
 * @returns {JSX.Element} The rendered ProductTour component.
 */
const ProductTour = ({ startTour }: IProductTourProps): JSX.Element => {
  const { userDetails } = useAppSelector(({ user: { userDetails } }: IApplicationState) => ({
    userDetails,
  }));
  const dispatch = useDispatch();
  const dispatcher = {
    setActiveKey: (data: string) => dispatch(SidebarActions.setSidebarKey(data)),
  };
  const setActiveKey = (data: string) => dispatcher.setActiveKey(data);
  let userTourSteps: Step[];
  if (!userDetails?.user_permissions.add_account) {
    userTourSteps = tourSteps.filter((toursteps) =>
      adminTourStepsOnly.every((steps) => steps !== toursteps.title),
    );
  } else {
    userTourSteps = tourSteps;
  }
  useEffect(() => {
    const cleanup = () => {
      setActiveKey(routes.cboDashboard);
      storage.setItem(STORAGE_CONSTANTS.first_login_response, 'false');
    };
    window.addEventListener('beforeunload', cleanup);
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
  }, []);

  return (
    <>
      <JoyRide
        steps={userTourSteps}
        continuous
        tooltipComponent={Tooltip}
        showProgress
        styles={joyRideGlobalStyles}
        run={startTour}
        disableScrolling={true}
        disableCloseOnEsc={true}
        disableOverlayClose={true}
      />
    </>
  );
};

export default ProductTour;
